import { z } from "zod";
import {
  Box,
  Text,
  WidgetEmpty,
  WidgetInteractive,
  WidgetSkeleton,
  compactNumber,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { resolveStoredTitle } from "../shared/derived-titles";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { STAGES_SOURCE } from "./stage-source";
import { useProjectsByStage } from "./use-projects-by-stage";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";

/** The specific sibling of the Projects by category tile: one stage, one
 *  number. Category answers "how many are done"; this answers "how many are
 *  in Roof inspection". */
export const projectsByStageConfigSchema = z.looseObject({
  stageId: z.string().nullish(),
  compact: z.boolean().default(false),
  filters: widgetFiltersSchema.nullish(),
});

export type ProjectsByStageConfig = z.infer<typeof projectsByStageConfigSchema>;

const GALLERY_LABEL = "Projects by stage";
const TITLE_PREFIX = "Projects in ";

/** "Projects in Roof inspection" — noun first, then the stage's own name, the
 *  way the category tile reads "Projects completed". */
function stageProjectsLabel(stageName: string): string {
  return `${TITLE_PREFIX}${stageName}`;
}

function ProjectsByStageRender({ h, config }: WidgetRenderContext<ProjectsByStageConfig>) {
  const { stageMap, workflowMap } = useWorkspaceData();
  // A stage in an archived workflow is no longer available, as the Stage
  // select says: the tile does not count it.
  const found = config.stageId ? stageMap.get(config.stageId) : undefined;
  const stage =
    found && !(found.workflowId && workflowMap.get(found.workflowId)?.archived) ? found : undefined;
  const stageId = stage?.stageId ?? null;
  const { value, loading, error } = useProjectsByStage(stageId, config.filters);
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (!stage) {
    return (
      <WidgetEmpty
        title={config.stageId ? "Stage no longer available" : "Pick a stage"}
        description="Choose the stage this tile counts in its settings."
      />
    );
  }
  if (loading) {
    return <WidgetSkeleton variant="stat" />;
  }

  const canDrill = Boolean(drill && instance);
  const openData = () => {
    if (!drill || !instance) return;
    drill.openData(instance.instanceId, {
      kind: "dimension",
      dimension: "stage",
      ids: [stage.stageId],
      label: stage.name,
    });
  };

  // The wash bleeds `xs` back over the body's inset on the sides and the
  // bottom alike — the metric tile's step — so it keeps an 8px ring to the
  // card's frame on all three edges; the stack runs with no gap to match.
  return (
    <Box fill="flex" align="center" justify="center" marginXNegative="xs" marginBottomNegative="xs">
      <WidgetInteractive
        fill="cell"
        rounded="lg"
        align="center"
        justify="center"
        cursor={canDrill ? "pointer" : undefined}
        hoverBackground={canDrill}
        onClick={canDrill ? openData : undefined}
      >
        <Box align="center" gap="none">
          <Text size={h >= 3 ? "4xl" : "2xl"} weight="bold" truncate={1}>
            {config.compact ? compactNumber(value) : value.toLocaleString()}
          </Text>
          <Text size="sm" variant="muted" truncate={1}>
            {stageProjectsLabel(stage.name)}
          </Text>
        </Box>
      </WidgetInteractive>
    </Box>
  );
}

const projectsByStageSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "stageId",
        kind: "select",
        label: "Stage",
        icon: PROPERTY_ICON.stage,
        dynamicOptions: STAGES_SOURCE,
        searchable: true,
        placeholder: "Pick a stage",
        missingLabel: "Stage no longer available",
        emptyLabel: "No stages yet",
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "compact", kind: "toggle", label: "Abbreviate large numbers", icon: "minimize" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const projectsByStageDef: WidgetModule<ProjectsByStageConfig> = {
  type: "projects-by-stage",
  title: GALLERY_LABEL,
  // The gallery name a placement stores and every "Projects in …" form the
  // tile writes re-derive on a Stage change; a member's own rename sticks.
  dynamicTitle: (config, context) => {
    const name = config.stageId ? context.optionLabel(STAGES_SOURCE, config.stageId) : undefined;
    return resolveStoredTitle(
      config,
      (title) => title === GALLERY_LABEL || title.startsWith(TITLE_PREFIX),
      name ? stageProjectsLabel(name) : GALLERY_LABEL,
    );
  },
  icon: PROPERTY_ICON.stage,
  span: { minW: 2, minH: 3, maxW: 12, maxH: 4, defaultW: 3, defaultH: 3 },
  configSchema: projectsByStageConfigSchema,
  settings: projectsByStageSettings,
  headerActions: WidgetHeaderFilters,
  bodyDraggable: true,
  catalog: [
    {
      key: "projects-by-stage.default",
      label: GALLERY_LABEL,
      description: "Counts the Projects in scope sitting in one stage of a workflow.",
      section: "stages" satisfies WidgetSection,
      tags: ["projects", "stages"] satisfies readonly WidgetTag[],
      defaultConfig: {},
      defaultW: 3,
    },
  ],
  render: ProjectsByStageRender,
};
